import React, { memo, FC } from "react";
import Typography from "@mui/material/Typography";
import Choices from "../Choices";
import ReusableAccordian from "../ReusableAccordion";
import { addScrollElements } from "../AccordionWrapper/AccordianWrapper";
import {
  addSdlChip,
  addTdlChip,
  addIRailChip,
  lites,
  lites2,
  litesIRail,
  litesAlum,
  extStopOptions,
} from "./defaults";
import DLAccordian from "./DLAccordian";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useLiteSelection from "./useLiteSelection";
import PleaseNote from "../PleaseNote";
import { useIsReadOnlyContextProviderContext } from "../../context/IsReadOnlyContext";
import { read } from "fs";

const ColLayoutTopDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  borderTop: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
}));

const StyledSpan = styled("span")({
  color: "red",
  fontSize: 20,
});

const LiteAccordian: FC = () => {
  const {
    handleClick,
    handleLitesClick,
    handleSDLExteriorShapeClick,
    materialChoice,
    doorType,
    liteConfig,
    sdlExteriorShape,
    addDL,
  } = useLiteSelection();
  const { readOnly } = useIsReadOnlyContextProviderContext();
  const isAlum = materialChoice === "All Aluminum";

  return (
    <>
      {addScrollElements(["Divided Lites Size"])}
      {addScrollElements(["SDL Exterior Shape"])}

      <ReusableAccordian
        accordionName="lite"
        heading="Lite Configuration"
        secondaryHeading="Select desired lite configuration"
      >
        {(materialChoice.includes("Clad") || isAlum) && (
          <ColLayout>
            <PleaseNote
              notes={[
                "Divided Lites will match Stickings/Stops profiles",
                "All shadow bars are available in black only",
              ]}
            />

            <RowLayout>
              <div
                onClick={() =>
                  readOnly ? null : handleClick(addSdlChip.label)
                }
              >
                <Choices
                  label={addSdlChip.label}
                  variant={addSdlChip.label === addDL ? "filled" : "outlined"}
                  checkMark={addSdlChip.label === addDL}
                  wide={true}
                  disabled={readOnly}
                />
              </div>
              {doorType === "Pivot" && (
                <div
                  onClick={() =>
                    readOnly ? null : handleClick(addIRailChip.label)
                  }
                >
                  <Choices
                    label={addIRailChip.label}
                    variant={
                      addIRailChip.label === addDL ? "filled" : "outlined"
                    }
                    checkMark={addIRailChip.label === addDL}
                    wide={true}
                    disabled={readOnly}
                  />
                </div>
              )}
            </RowLayout>

            {addDL === "Add SDL" && (
              <ColLayoutTopDivider>
                <Typography variant="subtitle1">
                  Simulated Divided Lites Size
                  <StyledSpan>*</StyledSpan>
                </Typography>
                <RowLayout>
                  {isAlum
                    ? litesAlum.map((state) => (
                        <div
                          key={state.id}
                          onClick={() =>
                            state.disabled || readOnly
                              ? null
                              : handleLitesClick(state.label)
                          }
                        >
                          <Choices
                            label={state.label}
                            variant={
                              state.label === liteConfig ? "filled" : "outlined"
                            }
                            checkMark={state.label === liteConfig}
                            disabled={state.disabled || readOnly}
                          />
                        </div>
                      ))
                    : lites.map((state) => (
                        <div
                          key={state.id}
                          onClick={() =>
                            state.disabled || readOnly
                              ? null
                              : handleLitesClick(state.label)
                          }
                        >
                          <Choices
                            label={state.label}
                            variant={
                              state.label === liteConfig ? "filled" : "outlined"
                            }
                            checkMark={state.label === liteConfig}
                            disabled={state.disabled || readOnly || false}
                          />
                        </div>
                      ))}
                </RowLayout>
                {materialChoice.includes("Clad") && liteConfig === '7/8"' && (
                  <RowLayout>
                    <Typography variant="subtitle1" style={{marginRight: "5px"}}>
                      SDL Exterior Shape
                      <StyledSpan>*</StyledSpan>
                    </Typography>
                    <Typography style={{marginRight: "10px"}}>
                      :
                    </Typography>
                    {extStopOptions.map((state) => (
                      <div
                        key={state.id}
                        onClick={() => state.disabled || readOnly ? null : handleSDLExteriorShapeClick(state.label)}
                      >
                        <Choices
                          label={state.label}
                          variant={
                            state.label === sdlExteriorShape ? "filled" : "outlined"
                          }
                          checkMark={state.label === sdlExteriorShape}
                          disabled={state.disabled || readOnly}
                        />
                      </div>
                    ))}
                  </RowLayout>
                )}
              </ColLayoutTopDivider>
            )}

            {doorType === "Pivot" && addDL === "Add Intermediate Rail" && (
              <ColLayoutTopDivider>
                <Typography variant="subtitle1">
                  Intermediate Rail Lites Size
                  <StyledSpan>*</StyledSpan>
                </Typography>
                <RowLayout>
                  {litesIRail
                    .filter((item) => item.label === liteConfig)
                    .map((state) => (
                      <div key={state.id}>
                        <Choices
                          label={state.label}
                          variant="filled"
                          checkMark={true}
                          disabled={true}
                        />
                      </div>
                    ))}
                </RowLayout>
              </ColLayoutTopDivider>
            )}
          </ColLayout>
        )}

        {materialChoice === "All Wood" && (
          <ColLayout>
            <PleaseNote
              notes={[
                "Divided Lites will match Stickings/Stops profiles",
                "All shadow bars are available in black only",
              ]}
            />

            <RowLayout>
              <div
                onClick={() =>
                  readOnly ? null : handleClick(addSdlChip.label)
                }
              >
                <Choices
                  label={addSdlChip.label}
                  variant={addSdlChip.label === addDL ? "filled" : "outlined"}
                  checkMark={addSdlChip.label === addDL}
                  wide={true}
                  disabled={readOnly}
                />
              </div>
              {doorType === "Pivot" ? (
                <div
                  onClick={() =>
                    readOnly ? null : handleClick(addIRailChip.label)
                  }
                >
                  <Choices
                    label={addIRailChip.label}
                    variant={
                      addIRailChip.label === addDL ? "filled" : "outlined"
                    }
                    checkMark={addIRailChip.label === addDL}
                    wide={true}
                    disabled={readOnly}
                  />
                </div>
              ) : (
                <div
                  onClick={() =>
                    readOnly ? null : handleClick(addTdlChip.label)
                  }
                >
                  <Choices
                    label={addTdlChip.label}
                    variant={addTdlChip.label === addDL ? "filled" : "outlined"}
                    checkMark={addTdlChip.label === addDL}
                    wide={true}
                    disabled={readOnly}
                  />
                </div>
              )}
            </RowLayout>
            <RowLayout>
              {addDL === "Add SDL" && (
                <ColLayoutTopDivider>
                  <Typography variant="subtitle1">
                    Simulated Divided Lites Size
                    <StyledSpan>*</StyledSpan>
                  </Typography>
                  <RowLayout>
                    {lites.map((state) => (
                      <div
                        key={state.id}
                        onClick={() =>
                          state.disabled || readOnly
                            ? null
                            : handleLitesClick(state.label)
                        }
                      >
                        <Choices
                          label={state.label}
                          variant={
                            state.label === liteConfig ? "filled" : "outlined"
                          }
                          checkMark={state.label === liteConfig}
                          disabled={state.disabled || readOnly}
                        />
                      </div>
                    ))}
                  </RowLayout>
                </ColLayoutTopDivider>
              )}
              {addDL === "Add TDL" && (
                <ColLayoutTopDivider>
                  <Typography variant="subtitle1">
                    True Divided Lites Size(upcharge)
                    <StyledSpan>*</StyledSpan>
                  </Typography>
                  <RowLayout>
                    <Choices
                      label={lites2.label}
                      variant="filled"
                      checkMark={true}
                      disabled={true}
                    />
                  </RowLayout>
                </ColLayoutTopDivider>
              )}
              {doorType === "Pivot" && addDL === "Add Intermediate Rail" && (
                <ColLayoutTopDivider>
                  <Typography variant="subtitle1">
                    Intermediate Rail Lites Size
                    <StyledSpan>*</StyledSpan>
                  </Typography>
                  <RowLayout>
                    {litesIRail
                      .filter((item) => item.label === liteConfig)
                      .map((state) => (
                        <div key={state.id}>
                          <Choices
                            label={state.label}
                            variant={
                              state.label === liteConfig ? "filled" : "outlined"
                            }
                            checkMark={state.label === liteConfig}
                            disabled={true}
                          />
                        </div>
                      ))}
                  </RowLayout>
                </ColLayoutTopDivider>
              )}
            </RowLayout>
          </ColLayout>
        )}
      </ReusableAccordian>

      {addScrollElements([
        "Daylight Opening",
        "Divided Lites Configuration",
        "Sidelite Daylight Opening",
        "Sidelite Divided Lites Configuration",
      ])}

      {materialChoice && addDL && <DLAccordian />}
    </>
  );
};

export default memo(LiteAccordian);
